import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, EnvelopeSimple, Spinner, ShieldStar, ToggleLeft, ToggleRight, MagnifyingGlass } from "@phosphor-icons/react";

const OWNER_EMAIL = "whoshonk@gmail.com";

export default function GoldTierPanel({ currentUser }) {
  const [status, setStatus] = useState(null); // tier info for the logged-in user
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", text: "" });
  const isOwner = currentUser?.email?.toLowerCase() === OWNER_EMAIL;

  // Admin-only state
  const [users, setUsers] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tier/me`, { withCredentials: true });
      setStatus(r.data);
    } catch (err) {
      console.error("Failed to fetch tier status:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAdminData = useCallback(async () => {
    if (!isOwner) return;
    setAdminLoading(true);
    try {
      const [uRes, rRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/tier-requests`, { withCredentials: true }),
      ]);
      setUsers(uRes.data.users || []);
      setReqs(rRes.data.requests || []);
    } catch (err) {
      console.error("Failed to load admin tier data:", err);
    } finally {
      setAdminLoading(false);
    }
  }, [isOwner]);

  useEffect(() => {
    fetchStatus();
    fetchAdminData();
  }, [fetchStatus, fetchAdminData]);

  const submitRequest = async () => {
    if (submitting) return;
    setSubmitting(true);
    setFeedback({ type: "", text: "" });
    try {
      await axios.post(`${API}/tier/request`, { message }, { withCredentials: true });
      setFeedback({ type: "success", text: "Request sent. The admin will review and get back to you." });
      setMessage("");
      await fetchStatus();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to send request";
      setFeedback({ type: "error", text: typeof detail === "string" ? detail : "Failed to send request" });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTier = async (userId, nextTier) => {
    try {
      await axios.post(`${API}/admin/user-tier`, { user_id: userId, tier: nextTier }, { withCredentials: true });
      // Optimistic update
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, tier: nextTier } : u));
      await fetchAdminData();
    } catch (err) {
      console.error("Failed to toggle tier:", err);
      alert("Failed to update tier — check console");
    }
  };

  if (loading) {
    return (
      <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-5" data-testid="gold-panel-loading">
        <Spinner className="animate-spin text-amber-400" />
      </div>
    );
  }

  const hasGold = status?.has_gold;
  const pending = status?.last_request?.status === "pending";
  const filteredUsers = users.filter(u =>
    !search ||
    (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.name || "").toLowerCase().includes(search.toLowerCase())
  );
  const pendingCount = reqs.filter(r => r.status === "pending").length;

  return (
    <div className="space-y-4" data-testid="gold-tier-panel">
      {/* User-facing status card */}
      <div className={`rounded-sm border p-5 ${hasGold ? "border-amber-500/50 bg-gradient-to-br from-amber-500/15 to-zinc-900/40" : "border-zinc-800 bg-zinc-900/40"}`}>
        <div className="flex items-start gap-3 mb-3">
          <ShieldStar size={22} weight={hasGold ? "fill" : "bold"} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading text-base font-bold text-zinc-100">Gold Tier Membership</h3>
              {hasGold && <Badge className="text-[9px] px-1.5 py-0 h-4 border-0 rounded-sm bg-amber-500/25 text-amber-300">ACTIVE</Badge>}
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Unlocks the <span className="text-amber-300 font-medium">Silly Goose</span> tab — a members-only playground that lives outside the serious stuff.
            </p>
          </div>
        </div>

        {hasGold ? (
          <div className="rounded-sm border border-emerald-500/25 bg-emerald-500/5 p-3 text-xs text-emerald-300 flex items-center gap-2" data-testid="gold-active-msg">
            <CheckCircle size={14} weight="fill" />
            You have Gold tier — the Silly Goose tab is now in your main nav.
          </div>
        ) : pending ? (
          <div className="rounded-sm border border-amber-500/25 bg-amber-500/5 p-3 text-xs text-amber-300" data-testid="gold-pending-msg">
            <p className="font-medium">Request pending</p>
            <p className="text-[11px] text-zinc-500 mt-1">Submitted {new Date(status.last_request.created_at).toLocaleString()}. You'll hear back shortly.</p>
          </div>
        ) : (
          <>
            <p className="text-[11px] text-zinc-500 mb-2 flex items-center gap-1.5">
              <EnvelopeSimple size={11} /> Access is granted manually. Send a short message and the admin (<span className="text-zinc-300">{OWNER_EMAIL}</span>) will review.
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 500))}
              data-testid="gold-request-textarea"
              placeholder="Optional: tell us a bit about why you'd like access..."
              className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm p-2 text-xs text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50 resize-none mb-2"
              rows={3}
            />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <button
                onClick={submitRequest}
                disabled={submitting}
                data-testid="gold-request-btn"
                className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
              >
                {submitting ? <Spinner size={12} className="animate-spin" /> : <EnvelopeSimple size={12} />}
                Request Gold Access
              </button>
              <span className="text-[10px] text-zinc-600">{message.length}/500</span>
            </div>
            {feedback.text && (
              <p className={`text-[11px] mt-2 ${feedback.type === "success" ? "text-emerald-400" : "text-red-400"}`} data-testid="gold-feedback">
                {feedback.text}
              </p>
            )}
          </>
        )}
      </div>

      {/* Admin-only control panel */}
      {isOwner && (
        <div className="rounded-sm border border-amber-500/40 bg-zinc-900/50 p-5" data-testid="gold-admin-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <h3 className="font-heading text-base font-bold text-zinc-100 flex items-center gap-2">
                <ShieldStar size={16} weight="fill" className="text-amber-400" />
                Admin · Gold Tier Access Control
              </h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Toggle Gold access per user. {pendingCount > 0 && <span className="text-amber-400 font-medium">{pendingCount} pending request{pendingCount === 1 ? "" : "s"}.</span>}
              </p>
            </div>
            <button
              onClick={fetchAdminData}
              data-testid="gold-admin-refresh"
              className="text-[10px] text-zinc-400 hover:text-zinc-200 px-2 py-1 border border-zinc-800 rounded-sm transition-colors"
            >
              Refresh
            </button>
          </div>

          {/* Pending requests summary */}
          {pendingCount > 0 && (
            <div className="mb-4 rounded-sm border border-amber-500/30 bg-amber-500/5 p-3" data-testid="gold-admin-pending">
              <p className="text-[11px] font-medium text-amber-300 mb-2">Pending Requests</p>
              <div className="space-y-1.5">
                {reqs.filter(r => r.status === "pending").map(r => (
                  <div key={r.id} className="flex items-start gap-2 text-[11px]">
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-200 truncate"><span className="font-medium">{r.name || r.email}</span> <span className="text-zinc-500">· {r.email}</span></p>
                      {r.message && <p className="text-zinc-500 text-[10px] mt-0.5 italic truncate">"{r.message}"</p>}
                    </div>
                    <button
                      onClick={() => toggleTier(r.user_id, "gold")}
                      data-testid={`gold-approve-${r.user_id}`}
                      className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition-colors shrink-0"
                    >
                      Grant
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative mb-3">
            <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users by email or name..."
              data-testid="gold-admin-search"
              className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm pl-7 pr-2 py-1.5 text-[11px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>

          {/* User list with toggle switches */}
          {adminLoading ? (
            <div className="text-center py-6"><Spinner size={14} className="animate-spin text-amber-400" /></div>
          ) : filteredUsers.length === 0 ? (
            <p className="text-[11px] text-zinc-600 text-center py-4">No users match your search.</p>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-1" data-testid="gold-admin-user-list">
              {filteredUsers.map(u => {
                const goldActive = u.tier === "gold";
                const isAdmin = u.email?.toLowerCase() === OWNER_EMAIL;
                return (
                  <div key={u.id} className="flex items-center justify-between gap-2 p-2.5 rounded-sm bg-zinc-950/40 border border-zinc-800/60 hover:border-zinc-700/60 transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] text-zinc-200 truncate font-medium">
                        {u.name || u.email}
                        {isAdmin && <span className="ml-1.5 text-[9px] text-amber-400">· OWNER</span>}
                      </p>
                      <p className="text-[10px] text-zinc-500 truncate">{u.email}</p>
                    </div>
                    <button
                      onClick={() => toggleTier(u.id, goldActive ? "standard" : "gold")}
                      disabled={isAdmin}
                      data-testid={`gold-toggle-${u.id}`}
                      title={isAdmin ? "Owner already has full access" : goldActive ? "Revoke Gold tier" : "Grant Gold tier"}
                      className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        goldActive
                          ? "text-amber-300 bg-amber-500/15 border-amber-500/40 hover:bg-amber-500/25"
                          : "text-zinc-400 bg-zinc-800/40 border-zinc-700/40 hover:border-zinc-600"
                      }`}
                    >
                      {goldActive ? <ToggleRight size={14} weight="fill" /> : <ToggleLeft size={14} />}
                      {goldActive ? "GOLD" : "Standard"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
